/**
 * The page an AI agent sends you to when it asks for access (OAuth consent): who asks, what it
 * may use, read-only or not. Allow returns to the agent with a one-time code; Deny refuses.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bot, LoaderCircle } from "lucide-react";
import { APP_NAME } from "@brainlog/shared";
import { errorMessage, get, post } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SpacePicker } from "./AgentsSettings";

export function ConnectPage() {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("client_id") ?? "";
  const redirectUri = params.get("redirect_uri") ?? "";
  const q = useQuery({
    queryKey: ["oauth-request", clientId, redirectUri],
    queryFn: () => get<{ client: { name: string; redirectHost: string }; spaces: { id: string; name: string; kind: string; encrypted: boolean; externalProcessingDisabled: boolean }[] }>(`/api/oauth/request?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`),
    retry: false,
  });
  const [spaces, setSpaces] = useState<string[] | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answer = async (approve: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ redirect: string }>("/api/oauth/approve", {
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: params.get("code_challenge") ?? "",
        state: params.get("state") ?? undefined,
        approve,
        workspaceIds: spaces,
        readOnly,
      });
      window.location.href = r.redirect;
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-4">
      <main className="w-full max-w-lg space-y-4 rounded-xl border bg-card p-6 shadow-sm">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{APP_NAME}</p>
        {q.isLoading && <p className="text-muted-foreground">Loading…</p>}
        {q.isError && (
          <p className="text-destructive" role="alert">
            {errorMessage(q.error)}
          </p>
        )}
        {q.data && (
          <>
            <h1 className="flex items-center gap-2 font-display text-2xl font-semibold">
              <Bot className="size-6" aria-hidden /> Connect {q.data.client.name}?
            </h1>
            <p className="text-sm">
              <strong>{q.data.client.name}</strong> asks to work in your {APP_NAME} as you: read, create notes, add papers and tasks, write and revise manuscripts, handle correspondence. It can{" "}
              <strong>never delete</strong>. It cannot open end-to-end encrypted notebooks and projects, or those with outside services turned off. Every change it makes is kept in the history.
            </p>
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">It may use</p>
              <SpacePicker spaces={q.data.spaces} value={spaces} onChange={setSpaces} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} /> Read only (it cannot change anything)
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              You return to <strong>{q.data.client.redirectHost}</strong>. You can disconnect it at any time in Settings → AI → Connect an AI app.
            </p>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => void answer(false)} disabled={busy}>
                Deny
              </Button>
              <Button onClick={() => void answer(true)} disabled={busy || (spaces !== null && spaces.length === 0)}>
                {busy && <LoaderCircle className="animate-spin" />} Allow
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
